import type { Dayjs } from 'dayjs'
import { dayjs } from '@/dayjs'
import type {
	ActivityLane,
	PositionedActivity,
	RoadmapActivity,
	SideValues,
	Timeline,
	TimelineColumn,
	TimelineMonth
} from './types'

// ─── Timeline ────────────────────────────────────────────────────────────────

const isWeekend = (date: Dayjs) => date.day() === 0 || date.day() === 6

/**
 * Monday-aligned start of week. dayjs' own `startOf('week')` follows the locale
 * (Sunday for `en`), which would shift every column off the reference roadmap,
 * and the isoWeek plugin isn't loaded in `@/dayjs`, so align by hand.
 */
const startOfIsoWeek = (date: Dayjs) => {
	const weekday = date.day()
	const deltaToMonday = weekday === 0 ? -6 : 1 - weekday
	return date.add(deltaToMonday, 'day').startOf('day')
}

/**
 * The chart's x axis: working days in order. Weekends are not on it at all, so
 * a week is exactly five units wide and bars land on column edges instead of
 * trailing two dead days.
 *
 * The axis is built out to whole weeks rather than stopping at the window, so a
 * window ending mid-week gets a full-width column instead of a stub: a year
 * starting on a Thursday shows its first week as Dec 29 to Jan 2, with the
 * three December days present but disabled. They exist to make the week
 * legible, and `firstEnabledIndex`/`lastEnabledIndex` fence off everything
 * that may actually hold work.
 *
 * Everything downstream measures in units (an index into `days`), never in
 * calendar days: the two only agree in a week with no weekend in it. Activity
 * dates stay real calendar dates in the config; this is a view-level axis.
 */
export const buildTimeline = (dateFrom: string, dateTo: string): Timeline => {
	const days: string[] = []
	// Whole weeks: back to the window's opening Monday, on to its closing Sunday.
	const axisEnd = startOfIsoWeek(dayjs(dateTo)).add(6, 'day')
	let cursor = startOfIsoWeek(dayjs(dateFrom))

	while (!cursor.isAfter(axisEnd)) {
		if (!isWeekend(cursor)) {
			days.push(cursor.format('YYYY-MM-DD'))
		}
		cursor = cursor.add(1, 'day')
	}

	const firstEnabledIndex = days.findIndex((day) => day >= dateFrom)
	let lastEnabledIndex = -1
	for (let index = days.length - 1; index >= 0; index--) {
		const day = days[index]
		if (day && day <= dateTo) {
			lastEnabledIndex = index
			break
		}
	}

	// An inverted or working-day-free window enables nothing, which every
	// consumer already reads as "there is nowhere to put this".
	const isEmpty = firstEnabledIndex === -1 || lastEnabledIndex === -1

	return {
		days,
		indexByDate: new Map(days.map((day, index) => [day, index])),
		totalUnits: days.length,
		firstEnabledIndex: isEmpty ? 0 : firstEnabledIndex,
		lastEnabledIndex: isEmpty ? -1 : lastEnabledIndex
	}
}

/**
 * Where a date sits on the axis. A date landing on a weekend has no unit of its
 * own, so it resolves to the nearest working day in `direction`: forward for
 * the start of a range, backward for its end, which also clips ranges that
 * overhang the window. Null when there is nothing in that direction.
 *
 * ISO dates compare lexicographically, which is why the scans below can use
 * plain string comparison.
 */
const resolveUnitIndex = (
	timeline: Timeline,
	date: string,
	direction: 'forward' | 'backward'
): number | null => {
	const exact = timeline.indexByDate.get(date)
	if (exact !== undefined) {
		return exact
	}

	if (direction === 'forward') {
		const index = timeline.days.findIndex((day) => day >= date)
		return index === -1 ? null : index
	}

	for (let index = timeline.days.length - 1; index >= 0; index--) {
		const day = timeline.days[index]
		if (day && day <= date) {
			return index
		}
	}
	return null
}

/** Total axis length. Floored at 1 so widths can never divide by zero. */
export const timelineTotalUnits = (timeline: Timeline) =>
	Math.max(timeline.totalUnits, 1)

/**
 * Resolves any dated range (activity or overlay) onto the axis, clipped to the
 * enabled window, never onto the week padding, which is scenery. Null when it
 * falls entirely outside, or entirely inside a weekend: in both cases there is
 * no unit to draw on.
 */
export const positionRange = (
	range: { dateFrom: string; dateTo: string },
	timeline: Timeline
): { offsetUnits: number; spanUnits: number } | null => {
	const rawStart = resolveUnitIndex(timeline, range.dateFrom, 'forward')
	const rawEnd = resolveUnitIndex(timeline, range.dateTo, 'backward')

	if (rawStart === null || rawEnd === null) {
		return null
	}

	const start = Math.max(rawStart, timeline.firstEnabledIndex)
	const end = Math.min(rawEnd, timeline.lastEnabledIndex)

	if (start > end) {
		return null
	}

	return { offsetUnits: start, spanUnits: end - start + 1 }
}

export const positionActivity = (
	activity: RoadmapActivity,
	timeline: Timeline
): PositionedActivity | null => {
	const positioned = positionRange(activity, timeline)
	return positioned ? { activity, ...positioned } : null
}

/**
 * Unit index of today, or null when today is outside the roadmap window. The
 * week padding does not count: it is not part of the roadmap, so standing on it
 * is the same as standing outside.
 */
export const todayUnitIndex = (timeline: Timeline): number | null => {
	const today = dayjs().format('YYYY-MM-DD')
	const first = timeline.days[timeline.firstEnabledIndex]
	const last = timeline.days[timeline.lastEnabledIndex]

	if (!(first && last) || today < first || today > last) {
		return null
	}

	// A weekend today resolves to the coming Monday, whose left edge is exactly
	// where Friday ended: the axis has no gap between them.
	return resolveUnitIndex(timeline, today, 'forward')
}

/**
 * Splits the axis into runs sharing a key. Days are ordered, so a run is always
 * contiguous and a group's offset is just its first index.
 */
const groupUnits = (timeline: Timeline, keyOf: (day: string) => string) => {
	const groups: { key: string; offsetUnits: number; spanUnits: number }[] = []

	timeline.days.forEach((day, index) => {
		const key = keyOf(day)
		const last = groups.at(-1)

		if (last && last.key === key) {
			last.spanUnits += 1
		} else {
			groups.push({ key, offsetUnits: index, spanUnits: 1 })
		}
	})

	return groups
}

/** Header columns: one per week, five working days wide. */
export const buildColumns = (timeline: Timeline): TimelineColumn[] =>
	groupUnits(timeline, (day) =>
		startOfIsoWeek(dayjs(day)).format('YYYY-MM-DD')
	).map((group) => {
		// Labelled from the week's own first and last working day, so a week
		// clipped by the window bounds says what it actually covers.
		const first = timeline.days[group.offsetUnits]
		const last = timeline.days[group.offsetUnits + group.spanUnits - 1]

		return {
			...group,
			label: `${dayjs(first).format('MMM D')} - ${dayjs(last).format('MMM D')}`
		}
	})

/** Month band above the columns. */
export const buildMonths = (timeline: Timeline): TimelineMonth[] =>
	groupUnits(timeline, (day) => dayjs(day).format('YYYY-MM')).map(
		(group) => ({
			...group,
			label: dayjs(group.key).format('MMMM'),
			tooltip: dayjs(group.key).format('MMMM YYYY'),
			// Wholly outside the window: a sliver of a month that is only on the
			// chart because the week padding reached into it.
			isPadding:
				group.offsetUnits + group.spanUnits - 1 <
					timeline.firstEnabledIndex ||
				group.offsetUnits > timeline.lastEnabledIndex
		})
	)

/**
 * Greedy interval packing: each activity drops into the first lane whose last
 * bar ends before it starts, otherwise it opens a new one. Overlapping work
 * stacks vertically instead of drawing on top of itself, which is what gives a
 * category its height.
 */
export const packIntoLanes = (
	positioned: PositionedActivity[]
): ActivityLane[] => {
	const lanes: ActivityLane[] = []
	const ordered = [...positioned].sort(
		(a, b) => a.offsetUnits - b.offsetUnits || a.spanUnits - b.spanUnits
	)

	for (const item of ordered) {
		const lane = lanes.find((candidate) => {
			const last = candidate.at(-1)
			if (!last) {
				return true
			}
			return last.offsetUnits + last.spanUnits <= item.offsetUnits
		})

		if (lane) {
			lane.push(item)
		} else {
			lanes.push([item])
		}
	}

	return lanes
}

// ─── Capacity / utilization ──────────────────────────────────────────────────

/**
 * Utilization per week column, keyed by column key, as a percentage per side.
 *
 * Each activity already carries its daily share of capacity (backend the
 * server did the maths once). A week's utilization is the sum of those shares
 * across the activities running in it, weighted by how many of the week's days
 * each one covers: an activity present every day counts fully, one present half
 * the week counts half. Weeks clipped by the roadmap window (the year's first
 * and last) count only their enabled days, so their ratio stays meaningful.
 */
export const buildColumnUtilization = (
	columns: TimelineColumn[],
	positioned: PositionedActivity[],
	timeline: Timeline
): Record<string, SideValues> =>
	Object.fromEntries(
		columns.map((column) => {
			// The week's schedulable stretch: its overlap with the enabled window.
			const weekStart = Math.max(
				column.offsetUnits,
				timeline.firstEnabledIndex
			)
			const weekEnd = Math.min(
				column.offsetUnits + column.spanUnits,
				timeline.lastEnabledIndex + 1
			)
			const capacityDays = Math.max(weekEnd - weekStart, 0)

			if (capacityDays === 0) {
				return [column.key, { backend: 0, frontend: 0 }]
			}

			const total = positioned.reduce(
				(sum, item) => {
					const overlapDays =
						Math.min(item.offsetUnits + item.spanUnits, weekEnd) -
						Math.max(item.offsetUnits, weekStart)
					if (overlapDays <= 0) {
						return sum
					}
					// The activity's daily share, counted once per day it covers,
					// then averaged over the week's days below.
					return {
						backend:
							sum.backend +
							item.activity.backendPercentage * overlapDays,
						frontend:
							sum.frontend +
							item.activity.frontendPercentage * overlapDays
					}
				},
				{ backend: 0, frontend: 0 }
			)

			return [
				column.key,
				{
					backend: total.backend / capacityDays,
					frontend: total.frontend / capacityDays
				}
			]
		})
	)

/**
 * Utilization band → a semaphore colour. A small dot, always beside the number,
 * so colour is never the only signal: green under 60, yellow to 80, red up to
 * full, purple at or over capacity. The thresholds mirror the guide's band
 * copy (guide_band_*), which is where they are explained to the reader.
 */
const UTILIZATION_DOT: Array<{ max: number; className: string }> = [
	{ max: 60, className: 'bg-success-500' },
	{ max: 80, className: 'bg-warning-400' },
	{ max: 100, className: 'bg-danger-500' }
]
const UTILIZATION_OVER = 'bg-purple-600'

export const utilizationDotClass = (utilization: number): string =>
	UTILIZATION_DOT.find((band) => utilization < band.max)?.className ??
	UTILIZATION_OVER
